import {
  fetchAllApplications,
  resolveAssignedApplicationsForSession,
} from "../../utils/matchesAssignedApplicationOrigin";
import type { AuthSessionUser } from "./authSession.types";

export const SSO_PREFETCH_PARAM = "variphi_sso_prefetch";
const SSO_PREFETCH_MESSAGE = "variphi_sso_prefetch_done";

export function isSsoPrefetchRequest(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get(SSO_PREFETCH_PARAM) === "1";
}

export function notifySsoPrefetchComplete(): void {
  if (typeof window === "undefined" || window === window.top) return;
  window.parent.postMessage(
    { type: SSO_PREFETCH_MESSAGE, origin: window.location.origin },
    "*",
  );
}

export async function prefetchSisterAppSessions(user: AuthSessionUser): Promise<void> {
  if (typeof window === "undefined" || isSsoPrefetchRequest()) return;

  const currentOrigin = window.location.origin.toLowerCase();
  let assigned = resolveAssignedApplicationsForSession(user);
  if (assigned.length === 0) {
    assigned = await fetchAllApplications();
  }
  const origins = new Map<string, string>();

  for (const app of assigned) {
    try {
      const origin = new URL(app.url).origin.toLowerCase();
      if (origin && origin !== currentOrigin && !origins.has(origin)) {
        origins.set(origin, app.url);
      }
    } catch {
      /* ignore invalid url */
    }
  }

  if (origins.size === 0) return;

  const pending = new Set(origins.keys());
  const timeout = window.setTimeout(() => cleanup(), 90_000);

  function cleanup(): void {
    window.clearTimeout(timeout);
    window.removeEventListener("message", onMessage);
    document
      .querySelectorAll("iframe[data-variphi-sso-prefetch]")
      .forEach((el) => el.remove());
  }

  function onMessage(event: MessageEvent): void {
    const data = event.data as { type?: string; origin?: string } | null;
    if (!data || data.type !== SSO_PREFETCH_MESSAGE) return;
    const origin = String(data.origin ?? "").toLowerCase();
    pending.delete(origin);
    if (pending.size === 0) cleanup();
  }

  window.addEventListener("message", onMessage);

  for (const appUrl of origins.values()) {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("data-variphi-sso-prefetch", "1");
    iframe.setAttribute("title", "SSO sync");
    iframe.style.cssText = "display:none;width:0;height:0;border:0";
    const base = appUrl.replace(/\/$/, "");
    iframe.src = `${base}/?${SSO_PREFETCH_PARAM}=1`;
    document.body.appendChild(iframe);
  }
}
